import { DialogTitle, Typography } from "@mui/material";
import { FieldProps, formatValueToOption } from "components/forms/Field";
import { DialogFormKit } from "components/forms/FormKit";
import { EntryStatusPill } from "components/layout/entries/EntryStatusPill";
import { ResponsiveDialog } from "components/modal";
import { Entry, Property } from "graphql/generated/types";
import { useFormContext } from "react-hook-form";
import * as EntryActions from "../../../../../../../actions/entry";
import { AthleteDetails } from "./AthleteDetails";
import { AthleteRegistrationDetails } from "./AthleteRegistrationDetails";
import { mapProperties } from "./mapProperties";
import { useDispatch } from "react-redux";
import { useAsyncTeamField } from "components/layout/forms/fields/SelectorField";
import { recursiveCamelToSnakeJS } from "utils/utils";
import { string } from "yup";
import { EntryDialogTeamMembers } from "./EntryDialogTeamMembers";

export type EntryDialogEntry = Entry & {
    cancelledDate?: Entry["createdAt"];
    properties?: Property[];
};

export interface EntryDialogProps {
    open: boolean;
    eventDivisionId: any;
    entry: EntryDialogEntry;
    showSeed?: boolean;
    seedIsPosition?: boolean;
    showBib?: boolean;
    isTeams: boolean;
    teamRoles: any;
    captureProperties?: Property[];
    onClose: () => void;
    removeButton: React.ReactNode;
}

export const EntryDialog: React.FC<EntryDialogProps> = ({
    open,
    onClose,
    ...props
}) => (
    <ResponsiveDialog
        open={open}
        className="lh-dialog"
        fullWidth={true}
        maxWidth="sm"
        onClose={onClose}
        aria-labelledby="entry-dialog-title"
        aria-describedby="entry-dialog-description"
    >
        <EntryDialogContent {...props} onClose={onClose} />
    </ResponsiveDialog>
);

export const EntryDialogContent: React.FC<Omit<EntryDialogProps, "open">> = ({
    eventDivisionId,
    entry,
    showSeed,
    seedIsPosition,
    showBib,
    onClose,
    removeButton,
    isTeams,
    teamRoles,
    captureProperties = [],
}) => {
    const [athleteProperties, registrationProperties, userProperties] =
        captureProperties.reduce(
            ([aPs, rPs, uPs], p) => {
                if (p.level === "athlete") aPs.push(p);
                if (p.level === "registration") rPs.push(p);
                if (p.level === "user") uPs.push(p);
                return [aPs, rPs, uPs];
            },
            [[] as Property[], [] as Property[], [] as Property[]],
        );
    const entryPropertyMapper = mapProperties(entry);

    const dispatch = useDispatch();
    const submit = (values: any) => {
        return EntryActions.updateEntry(
            eventDivisionId,
            entry.id,
            recursiveCamelToSnakeJS(values, ["_destroy", "properties"]),
        )(dispatch).then(onClose);
    };

    const field = useAsyncTeamField({
        autocompleteProps: { blurOnSelect: true },
        inputProps: { autoFocus: false },
    });

    const fields: FieldProps[] = [
        {
            name: "seed",
            type: "number",
            inputProps: {
                pattern: "[0-9]*",
                maxLength: "10",
            },
            label: seedIsPosition ? "Position" : "Seed",
            placeholder: entry.rank,
            renderIf: () => !!showSeed,
        },
        {
            name: "bib",
            type: "string",
            inputProps: {
                maxLength: "10",
            },
            label: "Bib",
            renderIf: () => !!showBib,
        },
        {
            label: "Team",
            name: "teamName",
            type: "creatableSearchSelect",
            ...field,
            format: (value, field) =>
                typeof value === "string"
                    ? value
                    : formatValueToOption(value, field),
            parse: (value) =>
                (typeof value === "string" ? value : value?.label) || null,
            validation: string().nullable(),
            onChange: ({ value, setValue }) => {
                if (typeof value?.value === "number") {
                    setValue("teamId", value?.value || null);
                }
            },
        },
        {
            name: "teamMembersAttributes",
            component: EntryDialogTeamMembers,
            renderIf: () => isTeams,
            teamRoles,
        },
    ];

    return (
        <>
            <DialogTitle
                id="entry-dialog-title"
                className="sticky-dialog-title"
            >
                <div className="title-top">
                    <Typography variant="h3">{entry.athlete.name}</Typography>
                    <EntryStatusPill status={"published"}>
                        {entry.status}
                    </EntryStatusPill>
                </div>
            </DialogTitle>

            <DialogFormKit
                fields={fields}
                onSubmit={submit}
                onCancel={onClose}
                initialValues={{
                    seed: entry.seed,
                    bib: entry.bib,
                    teamName: entry.teamName,
                    teamMembersAttributes: isTeams
                        ? entry.teamMembers
                        : undefined,
                    properties: entry.properties,
                }}
                buttonLabel="Save"
                secondaryAction={removeButton}
                footer={
                    <Footer
                        entry={entry}
                        isTeams={isTeams}
                        captureProperties={captureProperties}
                        athleteProperties={athleteProperties}
                        registrationProperties={
                            registrationProperties.map(
                                entryPropertyMapper,
                            ) as Property[]
                        }
                        userProperties={userProperties}
                    />
                }
            />
        </>
    );
};

interface FooterProps {
    entry: EntryDialogEntry;
    captureProperties: Property[];
    athleteProperties: Property[];
    registrationProperties: Property[];
    userProperties: Property[];
    isTeams: boolean;
}

const Footer = ({
    entry,
    captureProperties,
    athleteProperties,
    registrationProperties,
    userProperties,
    isTeams,
}: FooterProps) => {
    const { control, getValues, setValue } = useFormContext();
    const payments =
        entry.payment?.id && entry.payment.status === "success"
            ? [entry.payment]
            : undefined;
    return (
        <>
            <AthleteDetails
                athlete={entry.athlete}
                athleteProperties={athleteProperties}
                userProperties={userProperties}
                isTeams={isTeams}
            />
            <AthleteRegistrationDetails
                title="Registration details"
                control={control}
                getValues={getValues}
                setValue={setValue}
                entryId={entry.id}
                eventDivisionId={entry.eventDivisionId}
                entryDate={entry.createdAt}
                cancelledDate={entry.cancelledDate}
                payments={payments}
                basePath={"properties"}
                registrationProperties={registrationProperties}
                captureProperties={captureProperties}
            />
        </>
    );
};
